import { Input, Textarea, Button, ErrorNotice, Field, Modal } from '@kiipu/ui';
import { useEffect, useId, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, ClipboardText, Star } from '@phosphor-icons/react';

import { api, type Folder, type Tag } from '../api';
import { useToast } from '../context';
import { OrganizationPicker } from './organization-picker';
import { isHttpLink, shareLinks } from './organization-helpers';
import { canMutate, desktopBridge, readClipboardText, usePlatform } from '../lib/client-platform';
import { startDesktopCapture } from '../lib/desktop-captures';
import { useDesktopDirty } from '../lib/desktop-dirty-state';

export function SaveDialog({
  open,
  onOpenChange,
  folders,
  tags,
  folderId,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  folders: Folder[];
  tags: Tag[];
  folderId?: string;
  onSaved: (id: string) => void;
}) {
  const client = useQueryClient();
  const toast = useToast();
  const [url, setUrl] = useState('');
  const [folder, setFolder] = useState(folderId || '');
  const [favorite, setFavorite] = useState(false);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [selectedUrl, setSelectedUrl] = useState('');
  const [creating, setCreating] = useState(false);
  const [pasteError, setPasteError] = useState('');
  const links = shareLinks(url);
  const multipleLinks = links.length > 1;
  const captureUrl = multipleLinks ? selectedUrl : links[0] || '';
  const validUrl = isHttpLink(captureUrl);
  const optionsId = useId();
  const key = useRef(crypto.randomUUID());
  const previouslyOpen = useRef(false);
  usePlatform();
  useDesktopDirty('save-dialog', open && Boolean(url.trim()));
  useEffect(() => {
    if (open && !previouslyOpen.current && !url.trim()) {
      setFolder(folderId || '');
      key.current = crypto.randomUUID();
    }
    previouslyOpen.current = open;
  }, [open, folderId]);
  const mutation = useMutation({
    mutationFn: async () => {
      const input = { url: captureUrl, folderId: folder || null, tagIds, favorite };
      if (desktopBridge())
        return {
          kind: 'local' as const,
          job: await startDesktopCapture({ ...input, intent: 'save', key: key.current }),
        };
      return { kind: 'remote' as const, receipt: await api.capture(input, key.current) };
    },
    onSuccess: (result) => {
      void client.invalidateQueries({ queryKey: ['items'] });
      void client.invalidateQueries({ queryKey: ['usage'] });
      onOpenChange(false);
      setUrl('');
      setFavorite(false);
      setTagIds([]);
      setSelectedUrl('');
      setPasteError('');
      key.current = crypto.randomUUID();
      if (result.kind === 'local') {
        if (result.job.itemId) onSaved(result.job.itemId);
        toast('已加入本地保存，进度可在“本地保存”中查看。');
        return;
      }
      onSaved(result.receipt.item.id);
      toast(
        result.receipt.duplicate
          ? '内容已存在，已打开并保留原有整理信息。'
          : '链接已保存，正在获取正文。可以继续浏览。 ',
      );
    },
  });
  const disabled = mutation.isPending || creating || !canMutate();
  function changeUrl(value: string) {
    setUrl(value);
    setSelectedUrl('');
    setPasteError('');
    key.current = crypto.randomUUID();
    mutation.reset();
  }
  return (
    <Modal
      open={open}
      onOpenChange={(value) => {
        if (!disabled) onOpenChange(value);
      }}
      title="保存链接"
      description="粘贴链接或分享文字，一起选好文件夹与标签。"
    >
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          if (validUrl && !disabled) mutation.mutate();
        }}
      >
        <Field label="内容链接">
          <div className="save-share-field">
            <Textarea
              required
              autoFocus
              value={url}
              disabled={disabled}
              maxLength={16384}
              placeholder="粘贴文章、笔记、视频链接或分享文字"
              onChange={(event) => changeUrl(event.target.value)}
            />
            <button
              type="button"
              aria-label="从剪贴板粘贴"
              disabled={disabled}
              onClick={() => {
                void readClipboardText().then(
                  (text) => {
                    changeUrl(text);
                  },
                  () => setPasteError('无法读取剪贴板，请直接粘贴链接。'),
                );
              }}
            >
              <ClipboardText size={18} />
            </button>
          </div>
        </Field>
        {multipleLinks ? (
          <fieldset className="save-link-options" disabled={disabled}>
            <legend>检测到 {links.length} 个链接，请选择要保存的一条</legend>
            {links.map((link) => (
              <label className="save-link-option" key={link}>
                <Input
                  type="radio"
                  name={optionsId}
                  value={link}
                  checked={selectedUrl === link}
                  onChange={() => {
                    setSelectedUrl(link);
                    key.current = crypto.randomUUID();
                    mutation.reset();
                  }}
                />
                <span>
                  <strong>{isHttpLink(link) ? new URL(link).hostname : '无法识别来源'}</strong>
                  <span>{link}</span>
                </span>
              </label>
            ))}
          </fieldset>
        ) : null}
        {url.trim() && !links.length ? (
          <p className="field-hint">没有识别到完整的 HTTP 或 HTTPS 链接，请检查分享文字。</p>
        ) : !multipleLinks && captureUrl && url.trim() !== captureUrl ? (
          <p className="save-link-preview">将保存：{captureUrl}</p>
        ) : null}
        <OrganizationPicker
          folders={folders}
          tags={tags}
          folderId={folder}
          tagIds={tagIds}
          onFolderChange={(value) => {
            setFolder(value);
            key.current = crypto.randomUUID();
            mutation.reset();
          }}
          onTagsChange={(value) => {
            setTagIds(value);
            key.current = crypto.randomUUID();
            mutation.reset();
          }}
          disabled={disabled}
          onBusyChange={setCreating}
        />
        <label className="checkbox-row">
          <Input
            type="checkbox"
            checked={favorite}
            disabled={disabled}
            onChange={(event) => {
              setFavorite(event.target.checked);
              key.current = crypto.randomUUID();
              mutation.reset();
            }}
          />
          <Star size={15} />
          同时加入收藏
        </label>
        <p className="field-hint">
          支持公众号、知乎、小红书、微博、X、B 站、YouTube
          与通用文章。视频保存说明、封面与原始链接。
        </p>
        <ErrorNotice error={mutation.error || pasteError} />
        <div className="modal-actions">
          <Button onClick={() => onOpenChange(false)} disabled={disabled}>
            取消
          </Button>
          <Button
            type="submit"
            variant="primary"
            busy={mutation.isPending}
            disabled={!validUrl || disabled}
          >
            保存内容
            <ArrowRight size={15} />
          </Button>
        </div>
      </form>
    </Modal>
  );
}
